/**
 * Tables (design board 1; PostgreSQL and SQLite): the rows of one table with filters, sorting and pages; a cell edits
 * in place (double-click or Enter, Tab to the next), rows are added and marked for deletion. Nothing is written until
 * Apply: the changes wait in the pending bar, Review shows the statements, Apply runs them in one transaction (and the
 * panel offers Undo).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CaretDown, CaretUp, Export, FileCsv, MagnifyingGlass, Plus, Trash, X } from '@phosphor-icons/react';
import { EmptyState, ErrorState, Loading, ReadOnlyBadge, errorText, saveBlob } from './DataCommon';
import ReviewDialog from './ReviewDialog';
import CsvImportDialog from './CsvImportDialog';
import { getTableRows } from '../../services/DataService';
import {
  buildChanges, changeSources, displayValue, vectorText, editText, emptyPending, formatCount, parseCellInput, pendingCounts, pendingText, removeInsert,
  removeInserts, rowIdOf, stageEdit, stageInsert, stageInserts, toCsv, toggleDeletes, updateInsert,
} from '../../utils/dataModel';

// Imported rows shown as inputs in the table (the others wait in the pending bar, counted).
const IMPORTED_SHOWN = 10;

const PAGE = 50;
const OPS = ['=', '!=', '>', '>=', '<', '<=', 'like', 'is null', 'not null'];
const NUMERIC = /int|serial|numeric|decimal|real|float|double|money/i;

function FilterForm({ columns, onAdd, onCancel }) {
  const [column, setColumn] = useState(columns[0]?.name || '');
  const [op, setOp] = useState('=');
  const [value, setValue] = useState('');
  const needsValue = op !== 'is null' && op !== 'not null';
  const submit = (event) => {
    event.preventDefault();
    if (!column || (needsValue && value === '')) { return; }
    onAdd(needsValue ? { column, op, value } : { column, op });
  };
  return (
    <form aria-label="Add a filter" className="data-filter-form" onSubmit={submit}>
      <label className="data-sr" htmlFor="data-filter-column">Column</label>
      <select className="data-input" id="data-filter-column" onChange={(e) => setColumn(e.target.value)} value={column}>
        {columns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
      </select>
      <label className="data-sr" htmlFor="data-filter-op">Condition</label>
      <select className="data-input" id="data-filter-op" onChange={(e) => setOp(e.target.value)} value={op}>
        {OPS.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {needsValue && (
        <>
          <label className="data-sr" htmlFor="data-filter-value">Value</label>
          <input autoFocus className="data-input" id="data-filter-value" onChange={(e) => setValue(e.target.value)} placeholder="Value" value={value} />
        </>
      )}
      <button className="data-btn is-primary" type="submit">Add filter</button>
      <button className="data-btn is-ghost" onClick={onCancel} type="button">Cancel</button>
    </form>
  );
}

FilterForm.propTypes = { columns: PropTypes.array.isRequired, onAdd: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired };

function CellEditor({ initial, label, onCommit, onCancel }) {
  const [text, setText] = useState(initial);
  const done = useRef(false);
  const finish = (move) => {
    if (done.current) { return; }
    done.current = true;
    onCommit(text, move);
  };
  return (
    <input
      aria-label={label}
      autoFocus
      className="data-cell-input"
      onBlur={() => finish(0)}
      onChange={(e) => setText(e.target.value)}
      onFocus={(e) => e.target.select()}
      onKeyDown={(e) => {
        e.stopPropagation(); // the cell's own Enter would open the editor again
        if (e.key === 'Enter') { e.preventDefault(); finish(0); }
        if (e.key === 'Escape') { e.preventDefault(); done.current = true; onCancel(); }
        if (e.key === 'Tab') { e.preventDefault(); finish(e.shiftKey ? -1 : 1); }
      }}
      value={text}
    />
  );
}

CellEditor.propTypes = { initial: PropTypes.string, label: PropTypes.string, onCancel: PropTypes.func.isRequired, onCommit: PropTypes.func.isRequired };

export default function TablesView({
  projectId, store, table, schema = null, readOnly = false, initialFilter = null, stores = [], onApplied, onOpenQuery, onOpenObject, onPendingChange,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState(null);
  const [filters, setFilters] = useState(() => initialFilter || []);
  const [addingFilter, setAddingFilter] = useState(false);
  const [pending, setPending] = useState(emptyPending);
  const [editing, setEditing] = useState(null); // { rowId, column }
  const [selected, setSelected] = useState(() => new Set());
  const [review, setReview] = useState(false); // the Review dialog is open
  const [importing, setImporting] = useState(false);
  const [hidden, setHidden] = useState(() => new Set());
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const tableRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const answer = await getTableRows(projectId, store.id, table, {
        schema: schema || undefined, limit: PAGE, offset, sort: sort?.column, dir: sort?.dir, filter: filters,
      });
      setData(answer);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, store.id, table, schema, offset, sort, filters]);

  useEffect(() => { load(); }, [load, tick]);

  const counts = pendingCounts(pending);
  useEffect(() => { onPendingChange?.(counts.total); }, [counts.total]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(() => data?.columns || [], [data]);
  const shown = columns.filter((c) => !hidden.has(c.name));
  const primaryKey = data?.primaryKey?.length ? data.primaryKey : columns.filter((c) => c.pk).map((c) => c.name);
  const canEdit = !readOnly && !store.readOnly && primaryKey.length > 0;
  const canInsert = !readOnly && !store.readOnly;
  const rows = data?.rows || [];
  const total = Number(data?.total ?? rows.length);
  const qualified = schema && schema !== 'public' ? `${schema}.${table}` : table;
  const target = store.name && store.name !== store.kind ? `${store.name}.${table}` : table;

  const changes = useMemo(() => {
    const parsed = {
      ...pending,
      inserts: pending.inserts.map((ins) => ({
        ...ins,
        values: Object.fromEntries(Object.entries(ins.values).map(([col, text]) => [col, parseCellInput(text, columns.find((c) => c.name === col))])),
      })),
    };
    return buildChanges(parsed, { table, schema: schema || undefined });
  }, [pending, table, schema, columns]);
  const sources = useMemo(() => changeSources(pending), [pending]);
  const imported = pending.inserts.filter((ins) => ins.imported);
  const importInfo = useMemo(() => (imported.length ? { file: imported[imported.length - 1].imported, count: imported.length } : null), [imported.length, imported[imported.length - 1]?.imported]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownInserts = [...pending.inserts.filter((ins) => !ins.imported), ...imported.slice(0, IMPORTED_SHOWN)];

  const toggleSort = (column) => {
    setOffset(0);
    setSort((prev) => (prev?.column !== column ? { column, dir: 'asc' } : prev.dir === 'asc' ? { column, dir: 'desc' } : null));
  };

  const commitEdit = (row, column, text, move) => {
    const col = columns.find((c) => c.name === column);
    setPending((prev) => stageEdit(prev, row, primaryKey, column, parseCellInput(text, col)));
    if (move) {
      const editable = shown.map((c) => c.name);
      const next = editable[editable.indexOf(column) + move];
      setEditing(next ? { rowId: rowIdOf(row, primaryKey), column: next } : null);
    } else {
      setEditing(null);
    }
  };

  const valueOf = (row, column) => {
    const id = rowIdOf(row, primaryKey);
    const edit = id && pending.edits[id];
    return edit && column in edit.values ? edit.values[column] : row[column];
  };

  const allShownSelected = rows.length > 0 && rows.every((row) => selected.has(rowIdOf(row, primaryKey)));
  const toggleAll = () => setSelected(allShownSelected ? new Set() : new Set(rows.map((row) => rowIdOf(row, primaryKey)).filter(Boolean)));
  const toggleOne = (id) => setSelected((prev) => { const next = new Set(prev); if (next.has(id)) { next.delete(id); } else { next.add(id); } return next; });
  const markSelected = () => {
    const chosen = rows.filter((row) => selected.has(rowIdOf(row, primaryKey)));
    setPending((prev) => toggleDeletes(prev, chosen, primaryKey));
    setSelected(new Set());
  };

  const discard = () => { setPending(emptyPending()); setEditing(null); };
  const exportCsv = () => saveBlob(new Blob([toCsv(shown.map((c) => c.name), rows)], { type: 'text/csv' }), `${table}.csv`);

  const applied = (answer) => {
    setReview(false);
    setPending(emptyPending());
    setTick((t) => t + 1);
    const n = answer?.applied ?? changes.length;
    const cleared = answer?.cleared?.removed?.length ? ` · cleared ${answer.cleared.removed.length} Redis key${answer.cleared.removed.length === 1 ? '' : 's'}` : '';
    onApplied?.({ ...answer, summary: `${formatCount(n)} change${n === 1 ? '' : 's'} applied to ${table}${cleared}` });
  };

  const stageImport = ({ file, rows: list }) => {
    setPending((prev) => stageInserts(prev, list, { file }));
    setImporting(false);
  };
  const dropFailing = (indices) => {
    const ids = indices.map((i) => sources[i]?.insertId).filter(Boolean);
    setPending((prev) => removeInserts(prev, ids));
  };

  const onCellKey = (event, row, column) => {
    if (!canEdit) { return; }
    if (event.key === 'Enter' || event.key === 'F2') {
      event.preventDefault();
      setEditing({ rowId: rowIdOf(row, primaryKey), column });
    }
  };

  const fkTarget = (column, value) => {
    if (!column.fk || value === null || value === undefined) { return null; }
    return () => onOpenObject?.(column.fk.table, [{ column: column.fk.column, op: '=', value }]);
  };

  const editableNote = readOnly
    ? 'You can look but not change rows: you are a viewer of this project'
    : store.readOnly ? `${store.kind === 'sqlite' ? 'SQLite' : 'This store'} is read-only here` : null;

  return (
    <div className="data-view" data-testid="tables-view">
      <div className="data-view-head">
        <h1>{table}</h1>
        <span className="data-pill data-mono">{schema ? `${schema}.${table}` : table}</span>
        {data && <span className="data-pill">{formatCount(total)} row{total === 1 ? '' : 's'}</span>}
        {editableNote && <ReadOnlyBadge reason={editableNote} />}
        <span className="data-grow" />
        <button className="data-btn" onClick={() => onOpenQuery?.(`SELECT * FROM ${qualified} LIMIT 50;`)} type="button">
          <MagnifyingGlass aria-hidden size={15} />Query
        </button>
        {canInsert && (store.kind === 'postgres' || store.kind === 'mysql') && (
          <button className="data-btn" data-testid="import-csv" disabled={!columns.length} onClick={() => setImporting(true)} type="button"><FileCsv aria-hidden size={15} />Import CSV</button>
        )}
        <button className="data-btn" disabled={!rows.length} onClick={exportCsv} type="button"><Export aria-hidden size={15} />Export</button>
        {canInsert && (
          <button className="data-btn is-primary" onClick={() => setPending((prev) => stageInsert(prev))} type="button">
            <Plus aria-hidden size={15} />Add row
          </button>
        )}
      </div>

      <div className="data-card data-toolbar">
        {filters.map((f, index) => (
          <span className="data-chip" key={`${f.column}-${f.op}-${index}`}>
            {f.column} {f.op} {f.value !== undefined && <strong>{String(f.value)}</strong>}
            <button
              aria-label={`Remove filter ${f.column} ${f.op}${f.value !== undefined ? ` ${f.value}` : ''}`}
              className="data-btn is-ghost is-tiny"
              onClick={() => { setOffset(0); setFilters(filters.filter((_, i) => i !== index)); }}
              type="button"
            >
              <X aria-hidden size={12} />
            </button>
          </span>
        ))}
        {addingFilter && columns.length > 0 ? (
          <FilterForm
            columns={columns}
            onAdd={(f) => { setOffset(0); setFilters([...filters, f]); setAddingFilter(false); }}
            onCancel={() => setAddingFilter(false)}
          />
        ) : (
          <button className="data-btn is-ghost is-link" disabled={!columns.length} onClick={() => setAddingFilter(true)} type="button">+ Filter</button>
        )}
        <span className="data-grow" />
        <span className="data-muted">
          {sort ? `Sorted by ${sort.column} ${sort.dir === 'asc' ? '↑' : '↓'} · ` : ''}
          {data ? `${formatCount(total)} ${filters.length ? 'match' : 'in all'}` : ''}
        </span>
        {canEdit && selected.size > 0 && (
          <button className="data-btn is-danger" onClick={markSelected} type="button">
            <Trash aria-hidden size={15} />Delete {selected.size} row{selected.size === 1 ? '' : 's'}
          </button>
        )}
        <div className="data-menu-wrap">
          <button aria-expanded={columnsOpen} className="data-btn" disabled={!columns.length} onClick={() => setColumnsOpen(!columnsOpen)} type="button">Columns</button>
          {columnsOpen && (
            <fieldset className="data-menu">
              <legend className="data-sr">Columns shown</legend>
              {columns.map((c) => (
                <label key={c.name}>
                  <input
                    checked={!hidden.has(c.name)}
                    onChange={() => setHidden((prev) => { const next = new Set(prev); if (next.has(c.name)) { next.delete(c.name); } else { next.add(c.name); } return next; })}
                    type="checkbox"
                  />
                  {c.name}
                </label>
              ))}
            </fieldset>
          )}
        </div>
      </div>

      {error && <ErrorState error={error} onRetry={load} />}
      {!error && !data && loading && <Loading label={`Loading ${table}…`} />}
      {data && (
        <div className="data-card data-table-card">
          <div className="data-table-wrap" ref={tableRef}>
            <table className="data-table">
              <thead>
                <tr>
                  {canEdit && (
                    <th className="data-check-col">
                      <input aria-label="Select all shown" checked={allShownSelected} onChange={toggleAll} type="checkbox" />
                    </th>
                  )}
                  {shown.map((c) => (
                    <th aria-sort={sort?.column === c.name ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined} key={c.name} scope="col">
                      <button className="data-th-btn" onClick={() => toggleSort(c.name)} title={`${c.type}${c.pk ? ' · primary key' : ''}${c.fk ? ` · → ${c.fk.table}.${c.fk.column}` : ''}`} type="button">
                        {c.name}{c.fk ? ' →' : ''}
                        {sort?.column === c.name && (sort.dir === 'asc' ? <CaretUp aria-hidden size={11} /> : <CaretDown aria-hidden size={11} />)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shownInserts.map((ins, index) => (
                  <tr className="is-new" data-testid="new-row" key={ins.id}>
                    {canEdit && (
                      <td className="data-check-col">
                        <button aria-label="Remove this new row" className="data-btn is-ghost is-tiny" onClick={() => setPending((prev) => removeInsert(prev, ins.id))} type="button"><X aria-hidden size={12} /></button>
                      </td>
                    )}
                    {shown.map((c) => (
                      <td key={c.name}>
                        <input
                          aria-label={`New row ${index + 1}: ${c.name}`}
                          autoFocus={!ins.imported && c === shown.find((s) => !s.default && !s.pk) && index === pending.inserts.length - 1}
                          className="data-cell-input"
                          onChange={(e) => setPending((prev) => updateInsert(prev, ins.id, c.name, e.target.value))}
                          placeholder={c.default !== null && c.default !== undefined ? 'default' : c.nullable ? 'NULL' : ''}
                          value={ins.values[c.name] ?? ''}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                {imported.length > IMPORTED_SHOWN && (
                  <tr className="is-new" data-testid="imported-more">
                    <td colSpan={shown.length + (canEdit ? 1 : 0)}>
                      … and {formatCount(imported.length - IMPORTED_SHOWN)} more new rows from {importInfo?.file}
                      <button className="data-btn is-ghost is-link" onClick={() => setPending((prev) => removeInserts(prev, imported.map((ins) => ins.id)))} type="button">Remove the imported rows</button>
                    </td>
                  </tr>
                )}
                {rows.map((row, rowIndex) => {
                  const id = rowIdOf(row, primaryKey);
                  const deleted = id && pending.deletes[id];
                  const edit = id && pending.edits[id];
                  return (
                    <tr className={deleted ? 'is-deleted' : edit ? 'is-edited' : ''} data-testid="data-row" key={id || rowIndex}>
                      {canEdit && (
                        <td className="data-check-col">
                          <input aria-label={`Select row ${primaryKey.map((k) => row[k]).join(', ')}`} checked={selected.has(id)} onChange={() => toggleOne(id)} type="checkbox" />
                        </td>
                      )}
                      {shown.map((c) => {
                        const value = valueOf(row, c.name);
                        const isEditing = editing && editing.rowId === id && editing.column === c.name && !deleted;
                        const changed = edit && c.name in edit.values;
                        const open = fkTarget(c, value);
                        const label = `${c.name} of row ${primaryKey.map((k) => row[k]).join(', ')}`;
                        return (
                          <td
                            aria-label={canEdit && !deleted ? `${label}: ${displayValue(value)}. Press Enter to edit` : undefined}
                            className={`${NUMERIC.test(c.type || '') ? 'data-mono' : ''}${changed ? ' is-cell-edited' : ''}${value === null || value === undefined ? ' is-null' : ''}`}
                            key={c.name}
                            onDoubleClick={() => { if (canEdit && !deleted) { setEditing({ rowId: id, column: c.name }); } }}
                            onKeyDown={(e) => { if (!deleted) { onCellKey(e, row, c.name); } }}
                            tabIndex={canEdit && !deleted ? 0 : undefined}
                            title={changed ? `was ${displayValue(edit.original[c.name])}` : vectorText(value, c) ? String(value).slice(0, 2000) : undefined}
                          >
                            {isEditing ? (
                              <CellEditor
                                initial={editText(value)}
                                label={label}
                                onCancel={() => setEditing(null)}
                                onCommit={(text, move) => commitEdit(row, c.name, text, move)}
                              />
                            ) : open && !deleted ? (
                              <button className="data-link" onClick={open} title={`Open ${c.fk.table} where ${c.fk.column} = ${value}`} type="button">{displayValue(value)}</button>
                            ) : displayValue(value, c)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!rows.length && !pending.inserts.length && (
              <EmptyState title={filters.length ? 'No row matches these filters' : 'This table is empty'}>
                {canInsert && !filters.length ? 'Add a row, or let your service write some.' : null}
              </EmptyState>
            )}
          </div>
          <div className="data-table-foot">
            <span>
              {rows.length ? `Rows ${formatCount(offset + 1)}–${formatCount(offset + rows.length)} of ${formatCount(total)}` : 'No rows'}
              {canEdit ? ' · double-click a cell to edit · Tab to the next · type NULL for no value' : ''}
              {!readOnly && !store.readOnly && !primaryKey.length && columns.length > 0 ? ' · no primary key: rows can be added here, changed with Query' : ''}
            </span>
            <span className="data-row-gap">
              <button className="data-btn" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - PAGE))} type="button">Previous</button>
              <button className="data-btn" disabled={offset + rows.length >= total || loading} onClick={() => setOffset(offset + PAGE)} type="button">Next</button>
            </span>
          </div>
        </div>
      )}

      {counts.total > 0 && (
        <div aria-label="Pending changes" className="data-pending" data-testid="pending-bar" role="region">
          <strong>{counts.total} change{counts.total === 1 ? '' : 's'} not applied</strong>
          <span className="data-pending-text">{pendingText(pending)}</span>
          <span className="data-grow" />
          <button className="data-btn is-ghost is-on-dark" onClick={discard} type="button">Discard</button>
          <button className="data-btn" disabled={!changes.length} onClick={() => setReview(true)} type="button">Review SQL</button>
          <button className="data-btn is-primary" disabled={!changes.length} onClick={() => setReview(true)} type="button">Apply</button>
        </div>
      )}

      {review && changes.length > 0 && (
        <ReviewDialog
          changes={changes}
          importInfo={importInfo}
          onApplied={applied}
          onClose={() => setReview(false)}
          onDropFailing={dropFailing}
          projectId={projectId}
          sources={sources}
          store={store}
          stores={stores}
          table={table}
          target={target}
        />
      )}

      {importing && <CsvImportDialog columns={columns} onClose={() => setImporting(false)} onStage={stageImport} table={table} />}
    </div>
  );
}

TablesView.propTypes = {
  initialFilter: PropTypes.array,
  onApplied: PropTypes.func,
  onOpenObject: PropTypes.func,
  onOpenQuery: PropTypes.func,
  onPendingChange: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  schema: PropTypes.string,
  store: PropTypes.object.isRequired,
  stores: PropTypes.array,
  table: PropTypes.string.isRequired,
};
